import SmartBanner from '@/components/Common/SmartBanner/SmartBanner';
import { GlobalStoreCtx } from '@/stores/GlobalStore';
import { ScrollStoreCtx } from '@/stores/ScrollStore';
import clsx from 'clsx';
import { observer } from 'mobx-react-lite';
import React, { CSSProperties, isValidElement, useContext } from 'react';

import styles from './SunnyNav.module.scss';

type SunnyNavChildren = ((collapsed: boolean) => JSX.Element) | JSX.Element;

export interface ISunnyNavProps {
  /**
   * 접히는 내비게이션이면 `collapsed => JSX.Element`,
   * 정적 내비게이션이면 일반 요소 (단일 루트)
   */
  children: SunnyNavChildren;
  className?: string;
  style?: CSSProperties;
  legacyNav?: JSX.Element;
  legacyNavHeight?: number;
  isModal?: boolean;
  navHeight?: number;
}

function isStaticChildren(children: SunnyNavChildren): children is JSX.Element {
  return isValidElement(children);
}

/**
 * 디자인 시스템에 기반한 신규 네비게이션
 *
 * @example
 *  <SunnyNav
 *      legacyNavHeight={164}
 *      legacyNav={
 *        <CollapsingNav {...} />
 *      }
 *    >
 *      {collapsed => (
 *        <>
 *          <NavigationBar>
 *            {{
 *              left: <NavigationBarIconButton as="button" type="icon_arrowback" onClick={onClickBack} />,
 *              center: <NavigationBarHeading>{{ title, subs: [date, tripWayKor] }}</NavigationBarHeading>,
 *            }}
 *          </NavigationBar>
 *          <NavigationBar.Body collapsedClassName={styles.collapsedNavBody}>
 *            {...}
 *            <NavigationBar.BottomShadow />
 *          </NavigationBar.Body>
 *        </>
 *      )}
 *  </SunnyNav>
 */
const SunnyNav: React.FC<ISunnyNavProps> = ({
  children,
  className,
  style,
  legacyNav,
  legacyNavHeight = 44,
  navHeight = 48,
  isModal,
}) => {
  const globalStore = useContext(GlobalStoreCtx);
  const scrollStore = useContext(ScrollStoreCtx);

  if (globalStore.IsPc && legacyNav) {
    return (
      <>
        {legacyNav}
        <div style={{ paddingTop: legacyNavHeight }} />
      </>
    );
  }

  const showSmartBanner = !globalStore.IsPc && !isModal && globalStore.ShowSmartBanner;

  return (
    <>
      <section
        className={clsx(
          'sunny-nav',
          styles.container,
          showSmartBanner && scrollStore.IsGoingDown && styles.collapsed,
          className,
        )}
        style={style}
      >
        {showSmartBanner && <SmartBanner />}
        {isStaticChildren(children) ? children : children(scrollStore.IsGoingDown)}
      </section>
      <div style={{ paddingTop: navHeight }} />
    </>
  );
};

export default observer(SunnyNav);
